import { parseDay } from '@map-my-money/engine';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import type { PublicShare, SharedResult, ShowcaseData } from '../api/types.ts';
import { CompareChart } from '../components/charts/CompareChart.tsx';
import { Icon } from '../components/Icon.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { longSpan, monthYear, percent, yearOnly } from '../lib/format.ts';
import { rememberPlan } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { SharedFooter, SharedHeader, SharedMapView } from './SharedMap.tsx';

type Goal = SharedResult['goals'][number];

const REDACTED = '•••';
const goalName = (g: Goal) =>
  g.label && !g.label.includes(REDACTED) ? g.label : t(`share.goal.${g.series}` as MessageKey);

function headlineGoal(result: SharedResult): Goal | null {
  const moved = result.goals.filter((g) => (g.monthsFaster ?? 0) !== 0 || g.becomesAchievable);
  if (moved.length === 0) return null;
  return [...moved].sort((a, b) => (b.becomesAchievable ? 1000 : b.monthsFaster ?? 0) - (a.becomesAchievable ? 1000 : a.monthsFaster ?? 0))[0]!;
}

type LoadState = { status: 'loading' } | { status: 'ready'; share: PublicShare } | { status: 'missing' } | { status: 'error' };

/** A public page is never indexed and never leaks its token in a Referer. */
function usePublicPageHead(title: string | null) {
  useEffect(() => {
    const restore = [['robots', 'noindex, nofollow'], ['referrer', 'no-referrer']].map(([name, content]) => {
      const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      const meta = existing ?? Object.assign(document.createElement('meta'), { name });
      const previous = existing?.content ?? null;
      meta.content = content!;
      if (!existing) document.head.appendChild(meta);
      return () => (existing ? (meta.content = previous!) : meta.remove());
    });
    return () => restore.forEach((undo) => undo());
  }, []);
  useEffect(() => {
    const before = document.title;
    if (title) document.title = t('smap.docTitle', { title });
    return () => {
      document.title = before;
    };
  }, [title]);
}

export function ShareScreen() {
  const { token = '' } = useParams();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    api.publicShare(token).then(
      (share) => live && setState({ status: 'ready', share }),
      (e) => live && setState({ status: e instanceof ApiError && e.status === 404 ? 'missing' : 'error' }),
    );
    return () => {
      live = false;
    };
  }, [token, attempt]);
  usePublicPageHead(state.status === 'ready' ? state.share.title : null);

  if (state.status === 'ready' && state.share.kind !== 'showcase') {
    const share = state.share;
    const source = share.kind === 'map'
      ? { kind: 'map' as const, asOf: share.data.asOf, plan: share.data.plan, anchor: share.data.anchor ?? null }
      : { kind: 'template' as const, asOf: share.data.asOf, template: share.data };
    return (
      <div className="smap-page">
        <main id="main" className="smap-page__main">
          <SharedMapView source={source} meta={share} cta={<MakeItYours token={token} share={share} />} />
        </main>
      </div>
    );
  }
  if (state.status === 'loading') return <SharedSkeleton />;

  const owner = state.status === 'ready' ? state.share.ownerFirstName : null;
  return (
    <div className="share-page">
      <main className="share-page__column" id="main">
        <SharedHeader owner={owner} brand={state.status === 'ready' ? state.share.brand : null} />
        {state.status === 'missing' && (
          <div className="card card--pad-lg share-missing">
            <Icon name="link" size={22} />
            <h1 className="share-missing__title">{t('share.missing')}</h1>
            <p className="share-missing__body">{t('smap.missing.body')}</p>
            <a href="https://mapmymoney.app" className="button button--outline-mint">{t('smap.missing.cta')}</a>
          </div>
        )}
        {state.status === 'error' && (
          <div className="card card--pad-lg share-missing" role="alert">
            <Icon name="alert" size={22} className="text-amber" />
            <h1 className="share-missing__title">{t('smap.error.title')}</h1>
            <p className="share-missing__body">{t('smap.error.body')}</p>
            <button type="button" className="button button--primary" onClick={() => setAttempt((a) => a + 1)}>{t('common.retry')}</button>
          </div>
        )}
        {state.status === 'ready' && state.share.kind === 'showcase' && (
          <>
            <ShareBody share={state.share.data} title={state.share.title} owner={state.share.ownerFirstName ?? ''} />
            <div className="share-ctas">
              <Link to="/signin?next=/what-if" className="button button--primary button--glow button--block button--lg">
                <Icon name="whatIf" size={18} />{t('share.try')}
              </Link>
              <a href="https://mapmymoney.app" className="button button--white button--block button--lg">
                <Icon name="apple" size={18} />{t('share.getApp')}
              </a>
            </div>
          </>
        )}
        {state.status === 'ready' ? <SharedFooter owner={owner} expiresAt={state.share.expiresAt} /> : <p className="share-footer">{t('share.footer')}</p>}
      </main>
    </div>
  );
}

function SharedSkeleton() {
  return (
    <div className="smap-page">
      <main id="main" className="smap-page__main" aria-busy="true" aria-label={t('smap.loading')}>
        <div className="smap smap--skeleton">
          <div className="smap-top"><span className="skeleton smap-skel smap-skel--brand" /></div>
          <div className="skeleton smap-skel smap-skel--hero" />
          <div className="skeleton smap-skel smap-skel--chart" />
          <div className="smap-grid">
            <div className="smap-col"><div className="skeleton smap-skel smap-skel--card" /></div>
            <div className="smap-col"><div className="skeleton smap-skel smap-skel--card" /></div>
          </div>
        </div>
      </main>
    </div>
  );
}

// MARK: Make it yours

interface Access {
  readonly signUp: boolean;
  readonly signedIn: boolean;
}

type CtaState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'joined' }
  | { kind: 'copied'; message: string; planId: string }
  | { kind: 'error'; message: string; field?: boolean };

function useAccess(): Access | null {
  const [access, setAccess] = useState<Access | null>(null);
  useEffect(() => {
    let live = true;
    void Promise.allSettled([api.authMethods(), api.me()]).then(([methods, me]) => {
      if (!live) return;
      setAccess({
        signUp: methods.status === 'fulfilled' ? methods.value.signUp : false,
        signedIn: me.status === 'fulfilled' && me.value.access.cloud,
      });
    });
    return () => {
      live = false;
    };
  }, []);
  return access;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function MakeItYours({ token, share }: { token: string; share: PublicShare }) {
  const access = useAccess();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<CtaState>({ kind: 'idle' });
  const [email, setEmail] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLElement>(null);
  const titleId = useId();
  const emailId = useId();
  const errorId = useId();
  const signIn = `/signin?next=${encodeURIComponent(`/s/${token}`)}`;
  const waitlist = access !== null && !access.signedIn && !access.signUp;
  const canCopy = share.canFork;

  const join = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!EMAIL.test(email.trim())) {
      setState({ kind: 'error', message: t('smap.cta.emailInvalid'), field: true });
      emailRef.current?.focus();
      return;
    }
    setState({ kind: 'busy' });
    try {
      await api.joinWaitlist(email.trim(), `s:${token.slice(0, 6)}`);
      setState({ kind: 'joined' });
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      setState(status === 422
        ? { kind: 'error', message: t('smap.cta.emailInvalid'), field: true }
        : { kind: 'error', message: status === 429 ? t('smap.cta.rateLimited') : t('smap.cta.joinFailed') });
    }
  };

  const copy = useCallback(async () => {
    if (!access) return;
    if (!access.signedIn) {
      navigate(signIn);
      return;
    }
    setState({ kind: 'busy' });
    try {
      const result = await api.forkShare(token);
      if (result.becamePrimary) {
        rememberPlan(result.plan.id);
        toast.push({ tone: 'success', message: result.needsNumbers ? t('smap.cta.copiedPrimaryTemplate') : t('smap.cta.copiedPrimary') });
        navigate('/');
        return;
      }
      setState({ kind: 'copied', planId: result.plan.id, message: t(result.needsNumbers ? 'smap.cta.copiedNeedsNumbers' : 'smap.cta.copied', { name: result.plan.name }) });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        if (access.signUp) navigate(signIn);
        else setState({ kind: 'idle' });
        return;
      }
      const message = error instanceof ApiError && error.isPlanTooLarge
        ? error.message
        : error instanceof ApiError && error.code === 'plan_limit'
        ? t('smap.cta.planLimit', { limit: error.body?.limit ?? 5 })
        : error instanceof ApiError && error.status === 404
          ? t('smap.cta.gone')
          : t('smap.cta.copyFailed');
      setState({ kind: 'error', message });
    }
  }, [access, navigate, signIn, toast, token]);

  const dockAction = () => {
    if (waitlist) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => emailRef.current?.focus({ preventScroll: true }), 300);
    } else void copy();
  };

  const busy = state.kind === 'busy';
  const showCopy = access !== null && !waitlist && canCopy;
  return (
    <>
      <section ref={cardRef} className="card smap-card smap-cta" aria-labelledby={titleId}>
        <h2 className="smap-cta__title" id={titleId}>{t('smap.cta.title')}</h2>
        <p className="smap-cta__body">{t('smap.cta.body', { title: share.title })}</p>
        {access === null && <div className="skeleton smap-skel smap-skel--button" />}
        {waitlist && (state.kind === 'joined' ? (
          <p className="smap-cta__done" role="status"><Icon name="checkCircle" size={18} />{t('smap.cta.joined')}</p>
        ) : (
          <form className="smap-cta__form" onSubmit={(e) => void join(e)} noValidate>
            <label htmlFor={emailId} className="smap-cta__label">{t('smap.cta.email')}</label>
            <input
              ref={emailRef}
              id={emailId}
              className="input smap-cta__input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={t('smap.cta.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={state.kind === 'error' && state.field ? true : undefined}
              {...(state.kind === 'error' ? { 'aria-describedby': errorId } : {})}
            />
            {state.kind === 'error' && <p id={errorId} className="field__error" role="alert">{state.message}</p>}
            <button type="submit" className="button button--primary button--block button--lg" disabled={busy}>{t('smap.cta.join')}</button>
            <p className="smap-cta__fine">{t('smap.cta.privacy')}</p>
          </form>
        ))}
        {showCopy && (state.kind === 'copied' ? (
          <>
            <p className="smap-cta__done" role="status"><Icon name="checkCircle" size={18} />{state.message}</p>
            <button
              type="button"
              className="button button--primary button--block button--lg"
              onClick={() => {
                rememberPlan(state.planId);
                navigate('/');
              }}
            >
              {t('smap.cta.open')}<Icon name="arrowRight" size={17} />
            </button>
          </>
        ) : (
          <>
            <button type="button" className="button button--primary button--glow button--block button--lg" disabled={busy} onClick={() => void copy()}>
              <Icon name="copy" size={17} />{busy ? t('smap.cta.copying') : t('smap.cta.copy')}
            </button>
            {state.kind === 'error' && <p className="smap-cta__error" role="alert"><Icon name="alert" size={15} />{state.message}</p>}
            <p className="smap-cta__fine">{access.signedIn ? t('smap.cta.copyNote') : t('smap.cta.copySignIn')}</p>
          </>
        ))}
        <a className="smap-link smap-cta__how" href="https://mapmymoney.app">{t('smap.cta.how')}</a>
      </section>
      {access !== null && state.kind !== 'joined' && state.kind !== 'copied' && (waitlist || showCopy) && (
        <div className="smap-dock">
          <span className="smap-dock__text">
            <span className="smap-dock__title">{t('smap.cta.dock')}</span>
            <span className="smap-dock__sub">{waitlist ? t('smap.cta.inviteOnly') : t('smap.cta.yours')}</span>
          </span>
          <button type="button" className="button button--primary smap-dock__button" disabled={busy} onClick={dockAction}>
            {waitlist ? t('smap.cta.join') : t('smap.cta.copy')}
          </button>
        </div>
      )}
    </>
  );
}

function ShareBody({ share, title, owner }: { share: ShowcaseData; title: string; owner: string }) {
  const { result } = share;
  const goal = headlineGoal(result);
  const others = result.goals.filter((g) => g !== goal && ((g.monthsFaster ?? 0) > 0 || g.becomesAchievable)).slice(0, 2);
  const values = useMemo(() => {
    const base = result.yearly.filter((y) => y.baseline !== undefined).map((y) => ({ year: y.year, value: Number(y.baseline) }));
    const mod = result.yearly.filter((y) => y.modified !== undefined).map((y) => ({ year: y.year, value: Number(y.modified) }));
    if (base.length < 2 || mod.length < 2) return null;
    const start = parseDay(result.asOf);
    const at = (year: number) => (start + Math.round(year * 365.25)) as typeof start;
    return { baseline: base.map((p) => ({ date: at(p.year), value: p.value })), modified: mod.map((p) => ({ date: at(p.year), value: p.value })) };
  }, [result]);

  const sameYear = goal?.baselineDate && goal.modifiedDate && goal.baselineDate.slice(0, 4) === goal.modifiedDate.slice(0, 4);
  const when = (date: string) => (sameYear ? monthYear(parseDay(date)) : yearOnly(parseDay(date)));
  const headline = goal
    ? goal.becomesAchievable
      ? t('share.nowReachable')
      : (goal.monthsFaster ?? 0) > 0
        ? t('share.sooner', { span: longSpan(goal.monthsFaster!) })
        : t('share.later', { span: longSpan(-(goal.monthsFaster ?? 0)) })
    : result.impact.percent !== null
      ? percent(result.impact.percent, { sign: true, digits: Math.abs(result.impact.percent) < 10 ? 1 : 0 })
      : t('share.noChange');

  return (
    <>
      <div className="card share-title">
        <h1 className="share-title__text">“{title}”</h1>
        {owner && <p className="share-title__by">{t('share.by', { name: owner })}</p>}
      </div>
      <section className="card share-hero" aria-label={goal ? goalName(goal) : t('share.netWorthIn', { years: result.timeframeYears })}>
        <span className="eyebrow">{goal ? goalName(goal) : t('share.netWorthIn', { years: result.timeframeYears })}</span>
        <p className={`share-hero__headline ${goal && (goal.monthsFaster ?? 0) < 0 ? 'text-amber' : 'text-mint'}`}>{headline}</p>
        {goal && goal.modifiedDate && (
          <p className="share-hero__years">
            {goal.baselineDate && <><s className="share-hero__before">{when(goal.baselineDate)}</s><Icon name="arrowRight" size={16} /></>}
            <span className="share-hero__after">{when(goal.modifiedDate)}</span>
          </p>
        )}
        {share.showAmounts && values ? (
          <CompareChart
            baseline={values.baseline}
            modified={values.modified}
            height={150}
            compact
            zoom
            yearAxis="relative"
            label={t('share.chartValues', { years: result.timeframeYears })}
          />
        ) : goal ? (
          <DatesSketch goal={goal} asOf={result.asOf} />
        ) : null}
        <ul className="legend legend--center">
          <li><span className="legend__dash" aria-hidden="true" />{t('share.before')}</li>
          <li className="text-cyber strong"><span className="legend__swatch legend__swatch--line" style={{ background: 'var(--cyber)' }} aria-hidden="true" />{t('share.after')}</li>
        </ul>
      </section>
      <ul className="share-rows">
        {others.map((g) => (
          <li key={`${g.series}-${g.label}`} className="share-row">
            <span>{goalName(g)}</span>
            <span className="text-mint strong">{g.becomesAchievable ? t('share.nowReachable') : t('share.earlier', { span: longSpan(g.monthsFaster ?? 0) })}</span>
          </li>
        ))}
        {result.impact.percent !== null && (
          <li className="share-row">
            <span>{t('share.netWorthIn', { years: result.timeframeYears })}</span>
            <span className={`strong ${result.impact.percent >= 0 ? 'text-mint' : 'text-amber'}`}>
              {percent(result.impact.percent, { sign: true, digits: Math.abs(result.impact.percent) < 10 ? 1 : 0 })}
            </span>
          </li>
        )}
      </ul>
      {!share.showAmounts && (
        <p className="share-privacy">
          <Icon name="lock" size={14} />
          {t('share.amountsHidden', { name: owner || t('share.brand') })}
        </p>
      )}
    </>
  );
}

/** Without amounts only dates are known: two paths from today, ending at each date. */
function DatesSketch({ goal, asOf }: { goal: Goal; asOf: string }) {
  const start = parseDay(asOf);
  const before = goal.baselineDate ? parseDay(goal.baselineDate) : null;
  const after = goal.modifiedDate ? parseDay(goal.modifiedDate) : null;
  const end = Math.max(before ?? 0, after ?? 0, start + 365);
  const w = 350;
  const h = 100;
  const x = (d: number) => 16 + ((d - start) / (end - start)) * (w - 70);
  const falling = goal.series === 'debt';
  const y0 = falling ? 20 : 86;
  const y1 = falling ? 86 : 20;
  const curve = (endX: number) => `M16,${y0} C${16 + (endX - 16) * 0.45},${y0 + (y1 - y0) * 0.2} ${16 + (endX - 16) * 0.7},${y0 + (y1 - y0) * 0.75} ${endX},${y1}`;
  const label = t('share.chart', {
    goal: goalName(goal),
    before: before ? yearOnly(before) : '—',
    after: after ? yearOnly(after) : '—',
  });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="dates-sketch" role="img" aria-label={label}>
      <line x1={16} y1={falling ? 86 : 86} x2={w - 16} y2={86} className="chart__gridline" />
      {before && (
        <>
          <path d={curve(x(before))} fill="none" stroke="var(--ghost-3)" strokeWidth={2} strokeLinecap="round" strokeDasharray="5 5" />
          <text x={x(before) + 6} y={y1 + 4} className="dates-sketch__label dates-sketch__label--before">{yearOnly(before)}</text>
        </>
      )}
      {after && (
        <>
          <path d={curve(x(after))} fill="none" stroke="var(--cyber)" strokeWidth={2.5} strokeLinecap="round" className="chart__reveal" />
          <circle cx={x(after)} cy={y1} r={4} fill="var(--cyber)" stroke="var(--card)" strokeWidth={2} />
          <text x={x(after) + 7} y={y1 + (before && Math.abs(x(before) - x(after)) < 44 ? -8 : 4)} className="dates-sketch__label dates-sketch__label--after">{yearOnly(after)}</text>
        </>
      )}
    </svg>
  );
}
